import $ from 'jquery';
import moment from 'moment';
import { getTodayISO, addDays, formatDateISO } from '../utils/dateUtils';

export default function registerBookingListStore() {
  window.Alpine.store('bookingListStore', {
    selectedProducts: [],
    selectedStatuses: ['PENDING', 'CONFIRMED_PART', 'CONFIRMED', 'CANCELLED'],
    startDate: formatDateISO(addDays(getTodayISO(), -30)), // 30 days ago (timezone-safe)
    endDate: getTodayISO(), // Today (timezone-safe)
    dateFilterType: 'booking_created',
    searchQuery: '',
    bookings: [],
    currentPage: 1,
    bookingsPerPage: 25,
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    loading: false,
    filtersOpen: false, // Mobile filters panel state

    STATUS_DISPLAY: {
      PENDING: 'Pending',
      CONFIRMED_PART: 'Deposit',
      CONFIRMED: 'Confirmed',
      CANCELLED: 'Cancelled',
    },

    // Computed properties for pagination
    get paginationInfo() {
      const startItem =
        this.totalItems === 0
          ? 0
          : (this.currentPage - 1) * this.bookingsPerPage + 1;
      const endItem = Math.min(
        this.currentPage * this.bookingsPerPage,
        this.totalItems,
      );
      return {
        startItem,
        endItem,
        totalItems: this.totalItems,
        currentPage: this.currentPage,
        totalPages: this.totalPages,
      };
    },

    fetchBookings() {
      this.loading = true;

      // Prepare filter parameters
      const productFilters = this.selectedProducts.join(',');
      const statusFilters = this.selectedStatuses.join(',');

      const params = new URLSearchParams({
        start_date: this.startDate,
        end_date: this.endDate,
        date_filter_type: this.dateFilterType,
        page: this.currentPage,
        per_page: this.bookingsPerPage,
        product_filters: productFilters,
        status_filters: statusFilters,
        search: this.searchQuery.trim(),
      });

      fetch(`/bookings_app/fetch_bookings/?${params}`)
        .then((response) => response.json())
        .then((data) => {
          this.bookings = data.bookings;
          this.totalItems = data.pagination.total_items;
          this.totalPages = data.pagination.total_pages;
          this.hasNext = data.pagination.has_next;
          this.hasPrevious = data.pagination.has_previous;
          this.currentPage = data.pagination.current_page;

          console.log('Fetched bookings:', this.bookings);
          console.log('Pagination info:', data.pagination);
        })
        .catch((error) => {
          console.error('Error fetching bookings:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    initializeDatePicker(picker) {
      const updateDisplay = (start, end) => {
        picker.value = `${start.format('DD/MMM/YY')} - ${end.format(
          'DD/MMM/YY',
        )}`;
      };

      $(picker).daterangepicker(
        {
          startDate: moment(this.startDate, 'YYYY-MM-DD'),
          endDate: moment(this.endDate, 'YYYY-MM-DD'),
          locale: {
            format: 'DD/MMM/YY', // Display format
          },
          ranges: {
            Today: [moment(), moment()],
            Tomorrow: [moment().add(1, 'days'), moment().add(1, 'days')],
            Yesterday: [
              moment().subtract(1, 'days'),
              moment().subtract(1, 'days'),
            ],
            'Next 7 Days': [moment(), moment().add(6, 'days')],
            'Last 7 Days': [moment().subtract(6, 'days'), moment()],
            'Last 31 Days': [moment().subtract(30, 'days'), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
            'Last Month': [
              moment().subtract(1, 'month').startOf('month'),
              moment().subtract(1, 'month').endOf('month'),
            ],
          },
        },
        (start, end) => {
          this.startDate = formatDateISO(start.toDate()); // Store in ISO format (timezone-safe)
          this.endDate = formatDateISO(end.toDate());
          updateDisplay(start, end); // Update display format
          this.onFiltersChanged(); // Fetch bookings on date change
        },
      );

      // Initial display update
      updateDisplay(
        moment(this.startDate, 'YYYY-MM-DD'),
        moment(this.endDate, 'YYYY-MM-DD'),
      );
    },
    initializeProductFilters() {
      let allProductsDataElem = document.getElementById('allProductsData');
      let products = [];

      if (allProductsDataElem) {
        products = allProductsDataElem
          .getAttribute('data-products')
          .split(',')
          .map((product) => product.trim());
      }

      this.selectedProducts = products;

      console.log('Initial products:', this.selectedProducts);
    },
    // When filters change, reset to page 1 and fetch new data
    onFiltersChanged() {
      this.currentPage = 1;
      this.fetchBookings();
    },
    filterByProduct(productId) {
      if (this.selectedProducts.includes(productId)) {
        this.selectedProducts = this.selectedProducts.filter(
          (id) => id !== productId,
        );
      } else {
        this.selectedProducts.push(productId);
      }
      this.onFiltersChanged();
    },
    filterAllProducts() {
      let allProductsDataElem = document.getElementById('allProductsData');
      let products = [];

      if (allProductsDataElem) {
        products = allProductsDataElem
          .getAttribute('data-products')
          .split(',')
          .map((product) => product.trim());
      }

      // If all products are already selected, deselect all
      if (this.selectedProducts.length === products.length) {
        this.selectedProducts = [];
      } else {
        this.selectedProducts = products;
      }

      this.onFiltersChanged();
    },
    isProductSelected(productId) {
      return this.selectedProducts.includes(productId);
    },
    filterByStatus(status) {
      if (this.selectedStatuses.includes(status)) {
        this.selectedStatuses = this.selectedStatuses.filter(
          (s) => s !== status,
        );
      } else {
        this.selectedStatuses.push(status);
      }
      this.onFiltersChanged();
    },
    isStatusSelected(status) {
      return this.selectedStatuses.includes(status);
    },

    // Pagination methods
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchBookings();
      }
    },
    nextPage() {
      if (this.hasNext) {
        this.currentPage++;
        this.fetchBookings();
      }
    },
    previousPage() {
      if (this.hasPrevious) {
        this.currentPage--;
        this.fetchBookings();
      }
    },
    canGoNext() {
      return this.hasNext;
    },
    canGoPrevious() {
      return this.hasPrevious;
    },

    // Search methods
    onSearchChanged() {
      this.currentPage = 1; // Reset to first page when search changes
      this.fetchBookings();
    },
    clearSearch() {
      this.searchQuery = '';
      this.onSearchChanged();
    },

    statusDisplay(status) {
      return this.STATUS_DISPLAY[status] || status;
    },
    formatCurrency(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
    },
  });
}
